/**
 * `sceneDto.ts` — wire shape of a scene row in the
 * `projectstore-metadata` Dynamo table.
 *
 * Mirrors the public properties of Unity's `SceneData`
 * (`shared-application-code/.../Models/Data/SceneData.cs`) and the
 * Dynamo attribute mapping in `DynamoClient.ToSceneData`. Only the
 * fields the web client currently consumes are required; the rest
 * pass through `[k: string]: unknown` so `unmarshall` round-trips
 * without losing data.
 *
 * Required-vs-optional:
 *   - The four fields the projects-grid thumbnail flow needs are
 *     declared concretely (`ID` / `Index` / `ThumbnailID` /
 *     `ModifiedTimestamp`).
 *   - Everything else is opt-in; declare more typed fields when the
 *     scene-loading work in M7+ needs them.
 */

export interface SceneDto {
  /** Mirrors `SceneData.ID`. */
  readonly ID: string;
  /** Mirrors `SceneData.Name`. */
  readonly Name?: string;
  /**
   * Position in the scene list. Lowest `Index` is the project's
   * "cover" scene (`ProjectThumbnailsCache.cs:387`
   * `scenes.OrderBy(s => s.Index).FirstOrDefault()`).
   */
  readonly Index: number;
  /**
   * Cached thumbnail id. May be empty — the `LoadThumbnailsForProject`
   * path falls back to `SceneData.ID` (or
   * `GenerateThumbnailID(sceneID, supportsInstances, learnerCode)`)
   * when it's missing.
   */
  readonly ThumbnailID?: string;
  /**
   * ISO-8601 timestamp (`SceneData.ModifiedTimestamp`). Used for
   * cache invalidation: a thumbnail blob is re-fetched whenever the
   * scene's `ModifiedTimestamp` advances past the cached entry's
   * timestamp (mirrors `IsThumbnailCacheValid`).
   */
  readonly ModifiedTimestamp?: string;

  /** Mirrors `SceneData.PresenterNotes`, displayed as scene notes in Unity. */
  readonly PresenterNotes?: string;

  /**
   * Asset ids referenced by entities in this scene
   * (`SceneData.AssetIDs`). Populated by Unity when entities are
   * placed; the **per-scene** list is the source of truth for the
   * parts catalog (`ProjectDataModel.cs:870` — Unity loads parts
   * lazily for `scene.AssetIDs.Distinct()`). NOT to be confused with
   * `ProjectDto.AssetIDs`, which is rarely populated in production
   * accounts.
   *
   * Wire shape: Dynamo `L` (List) of `S` (String). May be missing /
   * empty for empty scenes.
   */
  readonly AssetIDs?: ReadonlyArray<string>;

  /**
   * Media asset ids referenced by media tiles in this scene
   * (`SceneData.MediaAssetIDs`). Tracked separately from `AssetIDs`
   * because media uses a different storage model (`MediaAssetFactory`).
   * Appended on text-tile create via
   * `IStorageModel.updateSceneMediaAssetIDs`.
   */
  readonly MediaAssetIDs?: ReadonlyArray<string>;

  /** Mirrors Unity `SceneData.TransitionSpeed`; default is `Quick`. */
  readonly TransitionSpeed?: string | number;
  /** Mirrors Unity `SceneData.ShowHiddenAs`: 0 = XRay, 1 = Invisible. */
  readonly ShowHiddenAs?: string | number;
  /** Mirrors Unity `SceneData.IsolatedEntityIDs`. */
  readonly IsolatedEntityIDs?: ReadonlyArray<string>;

  // -- Environment attributes (`DynamoClient.ToSceneData`, `DynamoClient.cs:1530`) --

  readonly GridVisible?: boolean;
  /** Per-device `ShadowMode` mask; preferred over the legacy bool. */
  readonly ShadowsVisiblePerDevice?: string | number;
  /** Legacy bool: `true` → `ShadowMode.All`, `false` → `None`. */
  readonly ShadowsVisible?: boolean;
  readonly ConsoleVisible?: boolean;
  readonly TableVisible?: boolean;
  readonly LightMode?: string | number;

  // -- Skybox attributes (`DynamoClient.UpdateSceneSkybox` / `UpdateSceneSkyboxTexture`) --

  readonly SkyboxMode?: string | number;
  readonly SolidColor?: Readonly<Record<string, number>>;
  readonly CustomColor?: Readonly<Record<string, number>>;
  readonly SkyboxIntensity?: string | number;
  readonly SkyboxRotation?: string | number;
  readonly SkyboxScale?: Readonly<Record<string, number>>;
  readonly SkyboxTextureID?: string;
  readonly SkyboxFormat?: string;
  readonly SkyboxTimestamp?: string;

  // -- Cutting-plane attributes (`DynamoClient.UpdateSceneCuttingPlane`,
  //    `DynamoClient.cs:2091`; read via `SafeGetBoolAttribute` /
  //    `SafeGetVector3Attribute` with false / (0,0,0) defaults) --

  readonly CuttingPlaneVisible?: boolean;
  readonly CuttingPlaneExist?: boolean;
  /** Root-space meters, Dynamo `M{x,y,z}`. */
  readonly CuttingPlanePosition?: Readonly<Record<string, number>>;
  /** Root-space euler degrees (no gizmo offset), Dynamo `M{x,y,z}`. */
  readonly CuttingPlaneRotation?: Readonly<Record<string, number>>;
  readonly UsePartColor?: boolean;

  /** Grab tool "Snap back" (`UpdateSceneSnapback`, BOOL, default true). */
  readonly ShouldSnapback?: boolean;

  // -- Scene-view camera attributes (`DynamoClient.UpdateSceneView`,
  //    `DynamoClient.cs:2130`; read in `ToSceneData` at `:1584-1586` via
  //    `SafeGetBoolAttribute(..., false)` / `SafeGetVector3Attribute`, which
  //    yields x/y/z = 0 for a MISSING attribute rather than an empty map) --

  /** Camera world position, Unity axes, Dynamo `M{x,y,z}`. */
  readonly SceneViewPosition?: Readonly<Record<string, number>>;
  /**
   * ⚠ Orbit PIVOT world position — NOT a rotation, despite the name. See
   * `SceneViewState` in `sceneInfo.ts` for the proof chain.
   */
  readonly SceneViewRotation?: Readonly<Record<string, number>>;
  readonly IsViewOrthographic?: boolean;

  readonly [extra: string]: unknown;
}

import type {
  CuttingPlaneState,
  SceneInfo,
  SceneTransitionSpeed,
  SceneViewState,
} from "./sceneInfo";
import { SCENE_TRANSITION_SPEED, SHADOW_MODE } from "./sceneInfo";
import { toNameFloatMap, vec3From } from "./nameFloatMap";
import type { RgbaColor, SkyboxData, SkyboxMode } from "./skyboxData";
import {
  DEFAULT_CUSTOM_SKYBOX_COLOR,
  DEFAULT_SOLID_SKYBOX_COLOR,
  isZeroColor,
  SKYBOX_MODE,
  SKYBOX_PNG_FORMAT,
} from "./skyboxData";

/**
 * Map a raw Dynamo `SceneDto` to the UI-shaped `SceneInfo` consumed
 * by the editor shell. Position is **server-supplied** (`Index`); the
 * 1-based rendering index is recomputed by `ProjectModel` after sort.
 */
export function mapSceneDtoToInfo(dto: SceneDto): SceneInfo {
  const skybox = mapSkyboxData(dto);
  return {
    id: dto.ID,
    name: dto.Name ?? "",
    index: typeof dto.Index === "number" ? dto.Index : 0,
    thumbnailUrl: null,
    entityCount: 0,
    modifiedAt: dto.ModifiedTimestamp ?? "",
    presenterNotes: dto.PresenterNotes ?? "",
    assetIds: dto.AssetIDs ?? [],
    mediaAssetIds: dto.MediaAssetIDs ?? [],
    transitionSpeed: mapSceneTransitionSpeed(dto.TransitionSpeed),
    hiddenObjectMode: mapHiddenObjectMode(dto.ShowHiddenAs),
    isolatedSnapshotIds: dto.IsolatedEntityIDs ?? [],
    gridVisible: dto.GridVisible ?? false,
    shadowsVisibleMask: mapShadowMask(dto),
    // Defaults mirror `DynamoClient.ToSceneData` (`DynamoClient.cs:1582-1583`):
    // console defaults on for default-skybox or gridded scenes, table
    // defaults on only for default-skybox scenes.
    consoleVisible:
      dto.ConsoleVisible ?? (skybox.mode === SKYBOX_MODE.None || (dto.GridVisible ?? false)),
    tableVisible: dto.TableVisible ?? skybox.mode === SKYBOX_MODE.None,
    lightMode: toNumber(dto.LightMode, 1),
    skybox,
    cuttingPlane: mapCuttingPlane(dto),
    // Unity `ToSceneData` reads ShouldSnapback with a TRUE default.
    shouldSnapback: dto.ShouldSnapback ?? true,
    sceneView: mapSceneView(dto),
  };
}

/**
 * Mirrors `ToSceneData`'s cutting-plane reads: bools via
 * `SafeGetBoolAttribute(..., false)`, vectors via `SafeGetVector3Attribute`
 * defaulting `{x:0,y:0,z:0}`.
 */
function mapCuttingPlane(dto: SceneDto): CuttingPlaneState {
  return {
    visible: dto.CuttingPlaneVisible ?? false,
    exist: dto.CuttingPlaneExist ?? false,
    position: vec3From(toNameFloatMap(dto.CuttingPlanePosition), [0, 0, 0]),
    euler: vec3From(toNameFloatMap(dto.CuttingPlaneRotation), [0, 0, 0]),
    usePartColor: dto.UsePartColor ?? false,
  };
}

/**
 * Mirrors `ToSceneData`'s scene-view reads (`DynamoClient.cs:1584-1586`):
 * the bool via `SafeGetBoolAttribute(..., false)` and both vectors via
 * `SafeGetVector3Attribute`, which fills x/y/z with 0 when the attribute is
 * absent — so an unset view is `(0,0,0)` / `(0,0,0)` / false, never null.
 * That all-zero shape IS the "no authored view" signal (`isSceneViewSet`).
 */
function mapSceneView(dto: SceneDto): SceneViewState {
  return {
    position: vec3From(toNameFloatMap(dto.SceneViewPosition), [0, 0, 0]),
    pivot: vec3From(toNameFloatMap(dto.SceneViewRotation), [0, 0, 0]),
    isOrthographic: dto.IsViewOrthographic ?? false,
  };
}

/**
 * Mirrors `ToSceneData`'s shadow fallback (`DynamoClient.cs:1558`):
 * prefer the per-device mask, else map the legacy bool to `All`/`None`.
 */
function mapShadowMask(dto: SceneDto): number {
  if (dto.ShadowsVisiblePerDevice !== undefined) {
    return toNumber(dto.ShadowsVisiblePerDevice, SHADOW_MODE.None);
  }
  return dto.ShadowsVisible ? SHADOW_MODE.All : SHADOW_MODE.None;
}

/**
 * Skybox attribute mapping mirroring `ToSceneData`'s inline `SkyboxData`
 * construction (`DynamoClient.cs:1532`), plus the backward-compat
 * normalization from `SkyboxContextViewModel.RefreshProperties`
 * (`(0,0,0,0)` colors and zero scale fall back to Unity defaults).
 */
export function mapSkyboxData(dto: SceneDto): SkyboxData {
  const solid = mapColor(dto.SolidColor);
  const custom = mapColor(dto.CustomColor);
  const scale = dto.SkyboxScale ?? {};
  const scaleX = typeof scale.x === "number" ? scale.x : 0;
  const scaleY = typeof scale.y === "number" ? scale.y : 0;
  return {
    mode: mapSkyboxMode(dto.SkyboxMode),
    solidColor: isZeroColor(solid) ? DEFAULT_SOLID_SKYBOX_COLOR : solid,
    customColor: isZeroColor(custom) ? DEFAULT_CUSTOM_SKYBOX_COLOR : custom,
    rotation: toNumber(dto.SkyboxRotation, 0),
    intensity: toNumber(dto.SkyboxIntensity, 1),
    scale: { x: scaleX === 0 ? 1 : scaleX, y: scaleY === 0 ? 1 : scaleY },
    textureId: dto.SkyboxTextureID ?? "",
    format: dto.SkyboxFormat ?? SKYBOX_PNG_FORMAT,
    modifiedAt: dto.SkyboxTimestamp ?? "",
  };
}

function mapSkyboxMode(value: unknown): SkyboxMode {
  const mode = toNumber(value, SKYBOX_MODE.None);
  return mode === SKYBOX_MODE.Solid || mode === SKYBOX_MODE.Custom
    ? mode
    : SKYBOX_MODE.None;
}

function mapColor(value: Readonly<Record<string, number>> | undefined): RgbaColor {
  return {
    r: typeof value?.r === "number" ? value.r : 0,
    g: typeof value?.g === "number" ? value.g : 0,
    b: typeof value?.b === "number" ? value.b : 0,
    a: typeof value?.a === "number" ? value.a : 0,
  };
}

function toNumber(value: unknown, fallback: number): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number.parseFloat(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
}

export function mapHiddenObjectMode(value: unknown): "xray" | "hidden" {
  if (typeof value === "number") return value === 1 ? "hidden" : "xray";
  switch (typeof value === "string" ? value.toLowerCase() : "") {
    case "1":
    case "invisible":
    case "hidden":
      return "hidden";
    case "0":
    case "xray":
    case "x-ray":
    default:
      return "xray";
  }
}

export function mapSceneTransitionSpeed(value: unknown): SceneTransitionSpeed {
  if (typeof value === "number" && Number.isInteger(value) && value >= 0 && value <= 4) {
    return value as SceneTransitionSpeed;
  }

  switch (typeof value === "string" ? value.toLowerCase() : "") {
    case "none":
      return SCENE_TRANSITION_SPEED.None;
    case "veryquick":
    case "very quick":
      return SCENE_TRANSITION_SPEED.VeryQuick;
    case "slow":
      return SCENE_TRANSITION_SPEED.Slow;
    case "veryslow":
    case "very slow":
      return SCENE_TRANSITION_SPEED.VerySlow;
    case "quick":
    default:
      return SCENE_TRANSITION_SPEED.Quick;
  }
}

export function sceneTransitionDurationSeconds(speed: SceneTransitionSpeed): number {
  switch (speed) {
    case SCENE_TRANSITION_SPEED.None:
      return 0;
    case SCENE_TRANSITION_SPEED.VeryQuick:
      return 0.5;
    case SCENE_TRANSITION_SPEED.Slow:
      return 2;
    case SCENE_TRANSITION_SPEED.VerySlow:
      return 4;
    case SCENE_TRANSITION_SPEED.Quick:
    default:
      return 1;
  }
}

/**
 * Length of the CAMERA move that accompanies a scene change — port of
 * `DesktopSceneModel.GetCameraTransitionTime` (`DesktopSceneModel.cs:484`).
 *
 * ⚠ This is deliberately NOT {@link sceneTransitionDurationSeconds}, and the
 * two disagree on exactly one value. Unity's camera switch has no
 * `case SceneTransitionSpeed.VeryQuick`, so VeryQuick falls through to the
 * `transitTime = 1f` initializer — while the part-morph table
 * (`ScenesTransitionModel.cs:106-118`) gives VeryQuick its own 0.5. Merging
 * them would make a VeryQuick scene's camera arrive twice as fast as the
 * desktop client's, which is the kind of drift nobody would ever look for.
 *
 * Any unrecognized speed also lands on 1, matching the `default` fall-through.
 */
export function cameraTransitionDurationSeconds(speed: SceneTransitionSpeed): number {
  switch (speed) {
    case SCENE_TRANSITION_SPEED.None:
      return 0;
    case SCENE_TRANSITION_SPEED.Slow:
      return 2;
    case SCENE_TRANSITION_SPEED.VerySlow:
      return 4;
    default:
      return 1;
  }
}

/**
 * Mirrors `ProjectModel.GenerateThumbnailID`
 * (`Models/ProjectModel/ProjectModel_Thumbnails.cs:14`):
 *
 *   `projectSupportsInstances ? "U#{learnerCode}${sceneID}" : sceneID`
 *
 * Web port today never sets `supportsInstances` (no instance / learner
 * flow yet), so callers normally pass the defaults and get the raw
 * scene id back.
 */
export function generateThumbnailId(
  sceneId: string,
  supportsInstances: boolean = false,
  learnerCode: string = "",
): string {
  return supportsInstances ? `U#${learnerCode}$${sceneId}` : sceneId;
}
